<template>
  <b-modal
    ref="modal-promessa"
    title="Promessa de Pagamento"
    size="lg"
    @show="onShow"
    @hide="onHide">

    <!-- Resumo das parcelas selecionadas -->
    <div class="parcelas-resumo mb-4 p-3 bg-light rounded">
      <h6>
        <i class="fas fa-list"></i>
        Parcelas Selecionadas ({{ parcelasSelecionadas.length }})
      </h6>
      <div class="table-responsive">
        <table class="table table-sm table-bordered mb-0">
          <thead class="thead-light">
            <tr>
              <th>Contrato</th>
              <th>Parcela</th>
              <th>Vencimento</th>
              <th>Dias Atraso</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="parcela in parcelasSelecionadas" :key="parcela.id">
              <td>{{ parcela.contratoCredito }}</td>
              <td>
                <span class="badge badge-info">{{ parcela.numeroParcela }}</span>
              </td>
              <td>{{ formatDate(parcela.dataVencimento) }}</td>
              <td class="text-center">
                <span :class="getClassDiasAtraso(parcela.quantidadeDiasAtraso)">
                  {{ parcela.quantidadeDiasAtraso }}
                </span>
              </td>
              <td class="text-right">
                <strong>{{ formatValue(getValorParcelaPromessa(parcela)) }}</strong>
              </td>
            </tr>
          </tbody>
          <tfoot class="table-info">
            <tr>
              <td colspan="4" class="text-right">
                <strong>Total:</strong>
              </td>
              <td class="text-right">
                <strong>{{ formatValue(valorTotal) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Formulário da promessa -->
    <b-form @submit.prevent="salvarPromessa">
      <div class="row">
        <div class="col-md-6">
          <b-form-group label="Data de Pagamento Prometida:" label-for="data-pagamento">
            <b-form-input
              id="data-pagamento"
              v-model="form.dataPagamentoPrometida"
              type="date"
              :min="dataMinima"
              :state="validation.dataPagamentoPrometida"
              required>
            </b-form-input>
            <b-form-invalid-feedback v-if="validation.dataPagamentoPrometidaMessage">
              {{ validation.dataPagamentoPrometidaMessage }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <b-form-group label="Observações:" label-for="observacoes">
            <b-form-textarea
              id="observacoes"
              v-model="form.observacoes"
              rows="3"
              placeholder="Descreva os detalhes da promessa de pagamento...">
            </b-form-textarea>
          </b-form-group>
        </div>
      </div>

      <!-- Configurações adicionais -->
      <div class="configuracoes-adicionais p-3 bg-light rounded">
        <b-form-group
          label="Mover card para:"
          label-for="coluna-destino"
          description="Opcional: selecione a coluna para onde o card deve ser movido quando a promessa vencer">
          <b-form-select
            id="coluna-destino"
            v-model="form.boardColumnDestinoId"
            :options="colunasBoardOptions">
            <template #first>
              <b-form-select-option :value="null">
                Não mover automaticamente
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
      </div>
    </b-form>

    <!-- Footer do modal -->
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-between">
        <div>
          <small class="text-muted">
            <i class="fas fa-info-circle"></i>
            A promessa será registrada para todas as parcelas selecionadas
          </small>
        </div>
        <div>
          <b-button variant="secondary" @click="cancelar">
            Cancelar
          </b-button>
          <b-button
            variant="success"
            @click="salvarPromessa"
            :disabled="!isFormValid || isSaving">
            <b-spinner v-if="isSaving" small></b-spinner>
            <i v-else class="fas fa-handshake"></i>
            {{ isSaving ? 'Salvando...' : 'Criar Promessa' }}
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import PromessaRepository from '@/shared/http/repositories/promessa/PromessaRepository'
import Tools from '@/components/tools/Tools.js'
import { toast } from '@/shared/utils/UIHelper'

export default {
  name: 'ModalPromessaPagamento',
  props: {
    parcelasSelecionadas: {
      type: Array,
      required: true
    },
    cardId: {
      type: Number,
      required: true
    },
    boardId: {
      type: Number,
      required: false,
      default: null
    },
    colunasBoard: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data () {
    return {
      form: {
        dataPagamentoPrometida: null,
        observacoes: '',
        criarFollowup: true, // Sempre true - follow-up deve ser criado obrigatoriamente
        boardColumnDestinoId: null
      },
      validation: {
        dataPagamentoPrometida: null,
        dataPagamentoPrometidaMessage: ''
      },
      isSaving: false
    }
  },
  computed: {
    isFormValid () {
      return this.form.dataPagamentoPrometida &&
             this.form.dataPagamentoPrometida >= this.dataMinima
    },

    dataMinima () {
      const hoje = new Date()
      return hoje.toISOString().split('T')[0]
    },

    valorTotal () {
      return this.parcelasSelecionadas.reduce((total, parcela) => {
        return total + (this.getValorParcelaPromessa(parcela) || 0)
      }, 0)
    },

    colunasBoardOptions () {
      if (!this.colunasBoard || this.colunasBoard.length === 0) {
        return []
      }

      return this.colunasBoard.map(coluna => ({
        value: coluna.id,
        text: coluna.nome || coluna.name || coluna.title
      }))
    }
  },
  methods: {
    /**
     * Valor correto para promessa de pagamento:
     * - Deve usar o valor atualizado da parcela (planilha: vlrATUALIZADO)
     * - No nosso modelo atual, isso vem em `valorParcela`
     * - `valorAtualizado` está sendo usado para o valor total da operação (vlrOperacao)
     */
    getValorParcelaPromessa (parcela) {
      if (!parcela) return 0
      // Prioridade: valorParcela (vlrATUALIZADO)
      if (parcela.valorParcela != null) return parcela.valorParcela
      // Fallback defensivo: se por algum motivo vier como snake/case diferente
      if (parcela.vlrAtualizado != null) return parcela.vlrAtualizado
      return 0
    },

    showModal () {
      this.$refs['modal-promessa'].show()
    },

    hideModal () {
      this.$refs['modal-promessa'].hide()
    },

    onShow () {
      this.limparForm()
    },

    onHide () {
      this.limparForm()
    },

    async salvarPromessa () {
      if (!this.validarFormulario()) {
        return
      }

      this.isSaving = true

      try {
        const parcelaIds = this.parcelasSelecionadas.map(p => p.id)

        const request = {
          parcelaIds: parcelaIds,
          dataPagamentoPrometida: this.form.dataPagamentoPrometida,
          observacoes: this.form.observacoes,
          cardId: this.cardId,
          criarFollowup: this.form.criarFollowup,
          boardColumnDestinoId: this.form.boardColumnDestinoId
        }

        const response = await PromessaRepository.criarPromessa(request)

        // Verificar se a resposta foi bem-sucedida
        if (response && response.status === 201) {
          toast.success('Promessa de pagamento criada com sucesso!')
          this.$emit('promessa-criada')
          this.hideModal()
        } else {
          toast.error('Erro ao criar promessa de pagamento')
        }
      } catch (error) {
        console.error('Erro ao criar promessa:', error)

        // Extrair mensagem de erro do backend
        let mensagemErro = 'Erro ao criar promessa de pagamento'

        // Neste projeto, o Axios retorna o erro direto em error.data
        if (error.data && typeof error.data === 'string') {
          mensagemErro = error.data
        } else if (error.response && error.response.data) {
          // Fallback para estrutura padrão do Axios
          if (typeof error.response.data === 'string') {
            mensagemErro = error.response.data
          } else if (error.response.data.message) {
            mensagemErro = error.response.data.message
          }
        } else if (error.message) {
          mensagemErro = error.message
        }

        toast.error(mensagemErro)
      } finally {
        this.isSaving = false
      }
    },

    cancelar () {
      this.hideModal()
    },

    validarFormulario () {
      this.validation = {
        dataPagamentoPrometida: true,
        dataPagamentoPrometidaMessage: ''
      }

      if (!this.form.dataPagamentoPrometida) {
        this.validation.dataPagamentoPrometida = false
        this.validation.dataPagamentoPrometidaMessage = 'Data de pagamento é obrigatória'
        return false
      }

      const dataPromessa = new Date(this.form.dataPagamentoPrometida)
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)

      // Data deve ser futura (não permite hoje)
      if (dataPromessa <= hoje) {
        this.validation.dataPagamentoPrometida = false
        this.validation.dataPagamentoPrometidaMessage = 'Data de pagamento deve ser uma data futura (a partir de amanhã)'
        return false
      }

      return true
    },

    limparForm () {
      this.form = {
        dataPagamentoPrometida: null,
        observacoes: '',
        criarFollowup: true, // Sempre true - follow-up deve ser criado obrigatoriamente
        boardColumnDestinoId: null
      }
      this.validation = {
        dataPagamentoPrometida: null,
        dataPagamentoPrometidaMessage: ''
      }
    },

    getClassDiasAtraso (dias) {
      if (dias > 90) return 'badge badge-danger'
      if (dias > 30) return 'badge badge-warning'
      return 'badge badge-info'
    },

    formatValue (value) {
      return Tools.formatMoneyBR(value)
    },

    formatDate (date) {
      if (!date) return '-'

      // Se a data vem como string
      if (typeof date === 'string') {
        // Extrair apenas a parte da data (antes do T ou espaço)
        const datePart = date.split('T')[0].split(' ')[0]

        // Verificar se está no formato yyyy-MM-dd
        if (datePart.includes('-')) {
          const [year, month, day] = datePart.split('-')
          return `${day}/${month}/${year}`
        }
      }

      // Fallback para objeto Date
      const d = new Date(date)
      return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    }
  }
}
</script>

<style scoped>
.parcelas-resumo {
  .table {
    font-size: 0.875rem;
  }
}

.configuracoes-adicionais {
  margin-top: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/views/private/crm/details/product/components/ModalPromessaPagamento.vue